import type { Optional } from '@festina-life/core'
import type { TreeNodeData } from '@festina-life/ui/listsAndTables'
import { type RefObject, useCallback, useEffect, useRef, useState } from 'react'

import type { ConfigurationTreeLeaf } from './types'

const PATH_ANCHOR_OFFSET_PX = 4

type Props = {
  treeContainerRef: RefObject<HTMLDivElement | null>
  stickyPathRef: RefObject<HTMLDivElement | null>
  tree: TreeNodeData<ConfigurationTreeLeaf>[]
  query: Optional<string>
  isVisible: boolean
}

export const useScrollPathTracking = ({
  isVisible,
  query,
  treeContainerRef,
  stickyPathRef,
  tree,
}: Props): {
  currentNodePath: Optional<string>
  isStickyPathVisible: boolean
} => {
  const animationFrameRef = useRef<Optional<number>>(undefined)
  const [currentNodePath, setCurrentNodePath] = useState<Optional<string>>()
  const [isStickyPathVisible, setIsStickyPathVisible] = useState(false)

  const updateCurrentNodePath = useCallback(() => {
    const container = treeContainerRef.current
    if (!container) {
      return
    }

    const scrollPathElements = container.querySelectorAll<HTMLElement>(
      '[data-tree-scroll-path]',
    )

    if (!scrollPathElements.length) {
      setCurrentNodePath(undefined)
      setIsStickyPathVisible(false)
      return
    }

    const containerRect = container.getBoundingClientRect()
    const stickyPathHeight = stickyPathRef.current?.offsetHeight ?? 0
    const anchorY = containerRect.top + stickyPathHeight + PATH_ANCHOR_OFFSET_PX

    // Filter to only visible elements to exclude
    // collapsed tree children that are still in the DOM.
    const visibleElements = Array.from(scrollPathElements).filter((element) =>
      element.checkVisibility(),
    )

    if (!visibleElements.length) {
      setCurrentNodePath(undefined)
      setIsStickyPathVisible(false)
      return
    }

    const firstElement = visibleElements[0]
    const firstElementBottom = firstElement.getBoundingClientRect().bottom
    const isFirstLineVisible = firstElementBottom > containerRect.top
    const hasScrolledPastSingleLine =
      container.scrollTop > firstElement.getBoundingClientRect().height

    setIsStickyPathVisible(hasScrolledPastSingleLine && !isFirstLineVisible)

    const currentElement = visibleElements.findLast(
      (element) => element.getBoundingClientRect().top <= anchorY,
    )

    const nextPath = currentElement?.dataset['treeScrollPath']

    setCurrentNodePath((previousPath) =>
      previousPath === nextPath ? previousPath : nextPath,
    )
  }, [treeContainerRef, stickyPathRef])

  useEffect(() => {
    const container = treeContainerRef.current
    if (!container) {
      return
    }

    const updateCurrentNodePathWithAnimationFrame = () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }

      animationFrameRef.current = requestAnimationFrame(() => {
        updateCurrentNodePath()
        animationFrameRef.current = undefined
      })
    }

    updateCurrentNodePathWithAnimationFrame()
    container.addEventListener(
      'scroll',
      updateCurrentNodePathWithAnimationFrame,
      {
        passive: true,
      },
    )

    return () => {
      container.removeEventListener(
        'scroll',
        updateCurrentNodePathWithAnimationFrame,
      )

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [tree, query, isVisible, updateCurrentNodePath, treeContainerRef])

  return { currentNodePath, isStickyPathVisible }
}
